import { Plus } from 'lucide-react'
import { useState } from 'react'

import { Button, Input } from '@/shared/components/ui'
import { useT } from '@/shared/i18n'
import { cn } from '@/shared/lib/cn'
import type { TaskCreateInput } from '@/shared/types/domain'

import { AssigneePicker } from './AssigneePicker'
import { DueDatePicker } from './DueDatePicker'

/**
 * El máximo que acepta `TaskCreate.title` en el backend.
 *
 * Está acá escrito porque el campo tiene que **impedir** pasarse, no avisar
 * después: el corte real es 200 y antes nada lo decía mientras se escribía.
 * Quien tipeaba 605 caracteres recibía un 422 genérico y el campo se vaciaba.
 */
export const MAX_TITULO = 200

export interface TaskQuickAddProps {
  projectId: string
  /** Devuelve una promesa para poder limpiar el campo **sólo si salió bien**. */
  onCreate: (input: TaskCreateInput) => Promise<unknown>
  pending?: boolean
  /** Arranca con el foco en el titulo. Lo pide el conmutador `⌘K`. */
  autoFocus?: boolean
  className?: string
}

/**
 * Alta en una linea: titulo, responsable y fecha, sin abrir un modal.
 *
 * El formulario NO se cierra al crear y el foco se queda en el titulo: cargar
 * la tanda de tareas de una reunion es el caso real, y cada modal que se abre y
 * se cierra es un motivo mas para volver a la planilla.
 */
export function TaskQuickAdd({
  projectId,
  onCreate,
  pending,
  autoFocus,
  className,
}: TaskQuickAddProps) {
  const t = useT()
  const [title, setTitle] = useState('')
  const [assigneeId, setAssigneeId] = useState<string | null>(null)
  const [dueDate, setDueDate] = useState<string | null>(null)

  /**
   * Crea, y **limpia el campo sólo si el servidor aceptó**.
   *
   * Antes se limpiaba siempre, en la línea siguiente al `onCreate`. Con un
   * título demasiado largo eso significaba: 422, un toast genérico, y el texto
   * que la persona acababa de escribir desaparecido. Una molestia convertida
   * en pérdida de trabajo por una línea mal puesta.
   */
  const submit = () => {
    const clean = title.trim()
    if (!clean) return
    void onCreate({
      project_id: projectId,
      title: clean,
      assignee_id: assigneeId,
      due_date: dueDate,
    })
      .then(() => setTitle(''))
      .catch(() => {
        /* El error lo muestra la mutación; acá lo único que importa es NO borrar. */
      })
  }

  return (
    <form
      className={cn(
        'hairline flex flex-wrap items-center gap-2 rounded-[var(--radius-card)] bg-surface-1 p-2',
        className,
      )}
      onSubmit={(event) => {
        event.preventDefault()
        submit()
      }}
    >
      <span className="flex min-w-[200px] flex-1 items-center gap-2">
        <Input
          value={title}
          autoFocus={autoFocus}
          maxLength={MAX_TITULO}
          onChange={(event) => setTitle(event.target.value)}
          placeholder={t('tasks.quickAdd.placeholder')}
          aria-label={t('tasks.quickAdd.titleLabel')}
          className="flex-1"
        />
        {/*
          El contador aparece recién cerca del límite: mostrarlo siempre sería
          ruido en el 99% de los títulos, que tienen cinco palabras. `aria-live`
          para que quien no lo ve también se entere de que se está acercando.
        */}
        {title.length >= MAX_TITULO - 30 ? (
          <span
            aria-live="polite"
            className={cn(
              'num shrink-0 text-2xs tabular-nums',
              title.length >= MAX_TITULO ? 'text-overdue' : 'text-text-3',
            )}
          >
            {t('tasks.quickAdd.charsLeft', { count: MAX_TITULO - title.length })}
          </span>
        ) : null}
      </span>
      <AssigneePicker value={assigneeId} onChange={setAssigneeId} projectId={projectId} />
      <DueDatePicker value={dueDate} onChange={setDueDate} />
      <Button type="submit" variant="primary" disabled={pending || title.trim() === ''}>
        <Plus size={13} aria-hidden="true" />
        {t('common.action.add')}
      </Button>
    </form>
  )
}
